import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getWishlist, removeFromWishlist } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Heart, Trash2, Loader2, BookOpen } from 'lucide-react';

export default function WishlistScreen(): React.JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    setLoading(true);
    try {
      const res = await getWishlist();
      setItems(res.wishlist || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load wishlist', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (courseId: number) => {
    try {
      await removeFromWishlist(courseId);
      setItems((prev) => prev.filter((i) => i.course_id !== courseId));
      toast.show('Removed from wishlist', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to remove', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-7 h-7 text-red-500" />
        <h1 className="text-2xl font-bold text-slate-900">My Wishlist</h1>
      </div>

      {items.length === 0 ? (
        <Card className="text-center py-12">
          <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Your wishlist is empty</h2>
          <p className="text-slate-500 mb-4">Save courses you're interested in for later.</p>
          <Link to="/">
            <Button leftIcon={<BookOpen className="w-4 h-4" />}>Browse Courses</Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => (
            <Card key={item.id} className="flex gap-4 items-center">
              <img
                src={item.course_image || '/placeholder-course.jpg'}
                alt={item.title}
                className="w-24 h-16 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <Link to={`/courses/${item.slug}`} className="font-semibold text-slate-900 hover:text-amber-500 truncate block">
                  {item.title}
                </Link>
                <p className="text-sm text-slate-500">
                  {item.discount_price > 0 ? (
                    <>
                      <span className="text-green-600 font-medium">₹{item.discount_price}</span>
                      <span className="line-through text-slate-400 ml-2">₹{item.price}</span>
                    </>
                  ) : (
                    <span className="font-medium">₹{item.price}</span>
                  )}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(item.course_id)}
                leftIcon={<Trash2 className="w-4 h-4 text-red-500" />}
              >
                Remove
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
